import React, { useState, useEffect } from 'react';
import CheckInsDisplay from './CheckInsDisplay';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';
import { Search, Filter } from 'lucide-react';

const CheckInList = () => {
  const { currentEvent } = useAppState();
  const [checkIns, setCheckIns] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  
  // Fetch check-ins when current event changes
  useEffect(() => {
    if (currentEvent?.id) {
      fetchCheckIns();
      fetchZones();
    } else {
      setCheckIns([]);
      setZones([]);
    }
  }, [currentEvent]);
  
  // Function to fetch zones for the event
  const fetchZones = async () => {
    if (!currentEvent?.id) return;
    
    try {
      const response = await api.get(`/events/${currentEvent.id}/zones/`);
      setZones(response.data);
    } catch (err) {
      console.error('Error fetching zones:', err);
      // Don't show error for zones as it's not critical
    }
  };
  
  // Function to fetch check-ins from API
  const fetchCheckIns = async (params = {}) => {
    if (!currentEvent?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/events/${currentEvent.id}/checkins/`, { 
        params 
      });
      setCheckIns(response.data);
    } catch (err) {
      console.error('Error fetching check-ins:', err);
      setError('Failed to load check-ins. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to export check-ins as CSV
  const exportCheckIns = async () => {
    if (!currentEvent?.id) return;

    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (selectedZone) {
        params.zone = selectedZone;
      }

      const response = await api.get(`/events/${currentEvent.id}/checkins/export/`, {
        params,
        responseType: 'blob', 
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      let filename = `checkins_${currentEvent.name}_${new Date().toISOString().slice(0, 10)}.csv`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error exporting check-ins:', err);
      setError('Failed to export check-ins. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle search and filter
  const handleSearchAndFilter = (e) => {
    e.preventDefault();
    
    const params = {};
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    if (selectedZone) {
      params.zone = selectedZone;
    }
    
    fetchCheckIns(params);
  };
  
  // Function to clear search and filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedZone('');
    fetchCheckIns();
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <div className="animate-pulse">Loading check-ins...</div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white shadow rounded-lg p-6 text-center text-red-600">
          {error}
          <button 
            onClick={() => fetchCheckIns()}
            className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!currentEvent) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          Please select an event to view check-ins.
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">{currentEvent.name} - Check-ins</h1>
        <div className="flex gap-2">
          <button
            onClick={() => exportCheckIns()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Export Check-ins
          </button>
          <button
            onClick={() => fetchCheckIns()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Data
          </button>
        </div>
      </div>
      
      {/* Search and Filter component */}
      <div className="mb-6 p-4 bg-white shadow rounded-lg">
        <form onSubmit={handleSearchAndFilter} className="flex flex-col md:flex-row gap-4">
          {/* Search input */}
          <div className="flex-grow relative">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by name, email, or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search size={16} />
            </span>
          </div>
          
          {/* Zone filter */}
          <div className="relative min-w-48">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value)}
            >
              <option value="">All Zones</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name} ({zone.code})
                </option>
              ))}
            </select>
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Filter size={16} />
            </span>
          </div>
          
          {/* Action buttons */}
          <div className="flex space-x-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Apply Filters
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Clear
            </button>
          </div>
        </form>
      </div>
      
      <CheckInsDisplay 
        checkIns={checkIns} 
        refreshCheckIns={fetchCheckIns}
        currentEvent={currentEvent}
      />
    </div>
  );
};

export default CheckInList;